import React, { useEffect, useMemo, useState } from "react";
import { api, formatINR, todayISO } from "@/lib/api";
import StatCard from "@/components/StatCard";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  BarChart,
  Bar,
  Legend,
} from "recharts";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import PeriodSelector, { rangeFromValue } from "@/components/PeriodSelector";

export default function Dashboard() {
  const [period, setPeriod] = useState({ mode: "last", n: 1 });
  const [vehicles, setVehicles] = useState([]);
  const [vehicleId, setVehicleId] = useState("__all");
  const [summary, setSummary] = useState(null);
  const [series, setSeries] = useState([]);
  const [investSummary, setInvestSummary] = useState(null);

  const { start, end } = useMemo(() => rangeFromValue(period), [period]);

  useEffect(() => {
    api.get("/vehicles").then((r) => setVehicles(r.data));
    api.get("/investments/summary").then((r) => setInvestSummary(r.data));
  }, []);

  useEffect(() => {
    const params = { start, end };
    const summaryUrl =
      vehicleId === "__all"
        ? "/summary/combined"
        : `/summary/vehicle/${vehicleId}`;
    const tsParams = vehicleId === "__all" ? params : { ...params, vehicle_id: vehicleId };
    Promise.all([
      api.get(summaryUrl, { params }),
      api.get("/summary/timeseries", { params: tsParams }),
    ]).then(([s, t]) => {
      setSummary(s.data);
      setSeries(t.data);
    });
  }, [vehicleId, start, end]);

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="label-eyebrow">Overview</div>
          <h1 className="font-display text-4xl md:text-5xl font-black tracking-tighter mt-1">
            Dashboard
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            {vehicleId === "__all" ? "All vehicles" : "Selected vehicle"} ·{" "}
            <span className="font-mono">{start}</span> →{" "}
            <span className="font-mono">{end}</span>
          </p>
        </div>
        <div className="flex gap-3 items-center">
          <Select value={vehicleId} onValueChange={setVehicleId}>
            <SelectTrigger className="w-[220px]" data-testid="dash-vehicle-select">
              <SelectValue placeholder="All vehicles" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all">All vehicles</SelectItem>
              {vehicles.map((v) => (
                <SelectItem key={v.id} value={v.id}>
                  {v.name} · {v.registration_number}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <PeriodSelector value={period} onChange={setPeriod} testid="dash-period" />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
        <StatCard
          label="Total Sales"
          value={formatINR(summary?.total_sales || 0)}
          sub={
            vehicleId === "__all"
              ? `Across ${summary?.per_vehicle?.length || 0} vehicles`
              : `${summary?.run_days || 0} run days · ${summary?.halt_days || 0} halts`
          }
          testid="stat-sales"
        />
        <StatCard
          label="Syndicate Deposit"
          value={formatINR(summary?.total_deposit || 0)}
          accent="text-blue-700"
          sub={
            vehicleId === "__all"
              ? "See per-vehicle below"
              : "Cumulative deposited"
          }
          testid="stat-deposit"
        />
        <StatCard
          label="Net Earnings"
          value={formatINR(summary?.total_earnings || 0)}
          accent="text-emerald-700"
          sub={
            (summary?.total_extra_income || 0) > 0
              ? `+ ${formatINR(summary?.total_extra_income || 0)} extra income`
              : `${summary?.entry_count || 0} entries`
          }
          testid="stat-earnings"
        />
        <StatCard
          label="Expenses"
          value={formatINR(summary?.total_expenses || 0)}
          accent="text-red-600"
          sub={`Savings bal ${formatINR(summary?.savings_balance || 0)}`}
          testid="stat-expenses"
        />
        <StatCard
          label="Profit"
          value={formatINR(summary?.profit || 0)}
          accent={
            (summary?.profit || 0) >= 0 ? "text-emerald-700" : "text-red-600"
          }
          sub="Earnings + Extra − Expenses"
          testid="stat-profit"
        />
      </div>

      {/* Investments (portfolio) — separate from operational profit */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="dashboard-investments">
        <StatCard
          label="Investments · Invested"
          value={formatINR(investSummary?.total_invested || 0)}
          sub={`${investSummary?.count || 0} holdings`}
          testid="stat-invest-invested"
        />
        <StatCard
          label="Investments · Current"
          value={formatINR(investSummary?.total_current || 0)}
          accent="text-blue-700"
          sub="Latest updated value"
          testid="stat-invest-current"
        />
        <StatCard
          label="Portfolio Gain/Loss"
          value={formatINR(investSummary?.gain_loss || 0)}
          accent={
            (investSummary?.gain_loss || 0) >= 0
              ? "text-emerald-700"
              : "text-red-600"
          }
          sub={
            (investSummary?.gain_loss || 0) >= 0
              ? "Unrealised gain"
              : "Unrealised loss"
          }
          testid="stat-invest-gainloss"
        />
        <StatCard
          label="Portfolio Return"
          value={`${(investSummary?.gain_pct || 0).toFixed(2)}%`}
          accent={
            (investSummary?.gain_pct || 0) >= 0
              ? "text-emerald-700"
              : "text-red-600"
          }
          sub="Not counted in operational profit"
          testid="stat-invest-return"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="card-swiss p-5" data-testid="chart-earnings-expenses">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="label-eyebrow">Series</div>
              <h3 className="font-display text-xl font-bold tracking-tight">
                Earnings vs Expenses
              </h3>
            </div>
          </div>
          <div className="h-[260px]">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={series}>
                <CartesianGrid stroke="#f1f5f9" vertical={false} />
                <XAxis dataKey="date" stroke="#9ca3af" fontSize={11} />
                <YAxis stroke="#9ca3af" fontSize={11} />
                <Tooltip
                  formatter={(v) => formatINR(v)}
                  contentStyle={{ borderRadius: 6, border: "1px solid #e5e7eb" }}
                />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="earnings"
                  stroke="#16a34a"
                  strokeWidth={2}
                  dot={false}
                  name="Earnings"
                />
                <Line
                  type="monotone"
                  dataKey="expenses"
                  stroke="#dc2626"
                  strokeWidth={2}
                  dot={false}
                  name="Expenses"
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="card-swiss p-5" data-testid="chart-sales-profit">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="label-eyebrow">Performance</div>
              <h3 className="font-display text-xl font-bold tracking-tight">
                Sales & Profit
              </h3>
            </div>
          </div>
          <div className="h-[260px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={series}>
                <CartesianGrid stroke="#f1f5f9" vertical={false} />
                <XAxis dataKey="date" stroke="#9ca3af" fontSize={11} />
                <YAxis stroke="#9ca3af" fontSize={11} />
                <Tooltip
                  formatter={(v) => formatINR(v)}
                  contentStyle={{ borderRadius: 6, border: "1px solid #e5e7eb" }}
                />
                <Legend />
                <Bar dataKey="sales" fill="#2563eb" name="Sales" radius={[4, 4, 0, 0]} />
                <Bar dataKey="profit" fill="#0a0a0a" name="Profit" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {vehicleId === "__all" && summary?.per_vehicle?.length > 0 && (
        <div className="card-swiss p-5" data-testid="per-vehicle-breakdown">
          <div className="label-eyebrow mb-4">Per Vehicle</div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 text-left text-xs uppercase tracking-wider text-gray-500">
                  <th className="py-2 pr-4 font-semibold">Vehicle</th>
                  <th className="py-2 px-2 text-right font-semibold">Run Days</th>
                  <th className="py-2 px-2 text-right font-semibold">Sales</th>
                  <th className="py-2 px-2 text-right font-semibold">Deposit</th>
                  <th className="py-2 px-2 text-right font-semibold">Earnings</th>
                  <th className="py-2 px-2 text-right font-semibold">Expenses</th>
                  <th className="py-2 px-2 text-right font-semibold">Profit</th>
                  <th className="py-2 px-2 text-right font-semibold">Savings Bal</th>
                </tr>
              </thead>
              <tbody>
                {summary.per_vehicle.map((row) => (
                  <tr key={row.vehicle.id} className="border-b border-gray-100 hover-row">
                    <td className="py-3 pr-4">
                      <div className="font-semibold">{row.vehicle.name}</div>
                      <div className="text-xs text-gray-500 font-mono">
                        {row.vehicle.registration_number}
                      </div>
                    </td>
                    <td className="py-3 px-2 text-right font-mono">{row.run_days}</td>
                    <td className="py-3 px-2 text-right font-mono">
                      {formatINR(row.total_sales)}
                    </td>
                    <td className="py-3 px-2 text-right font-mono text-blue-700">
                      {formatINR(row.total_deposit)}
                    </td>
                    <td className="py-3 px-2 text-right font-mono text-emerald-700">
                      {formatINR(row.total_earnings)}
                    </td>
                    <td className="py-3 px-2 text-right font-mono text-red-600">
                      {formatINR(row.total_expenses)}
                    </td>
                    <td className="py-3 px-2 text-right font-mono font-semibold">
                      {formatINR(row.profit)}
                    </td>
                    <td className="py-3 px-2 text-right font-mono">
                      {formatINR(row.savings_balance)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
